import { convertDateRangeToDates } from "@wingspanhq/fe-component-library";
import {
  ListFormsSortParameterValue,
  PayableStatus,
  PayerApprovalStatus,
  SearchInvoiceRowsSortParameter,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import { UrlQuerySort } from "../../../../../shared/hooks/useUrlQuery";
import { getApiDirectionFromTable } from "../../../../../shared/utils/sorting";
import { Filter, PayablesTabFilter, SortField } from "./types";

/**
 * The v3 `searchPayableRows` request the payables table builds — a filter plus
 * an optional sort. Pagination is managed by the registry paginated-query hook,
 * so `page` is intentionally omitted here.
 */
export type PayableRowsSearchRequest = {
  filter: SearchPayableRowsFilterParameter;
  sort?: SearchInvoiceRowsSortParameter;
};

// v1 payable statuses `Pending`/`Open`/`Overdue` collapse onto the v3
// `PayableStatus` values that back the pre-payment tabs (`Pending`, `Opened`);
// v3 has no separate `Overdue` (it is derived from `Opened` + a past due date).
const PRE_PAYMENT_STATUSES: PayableStatus[] = [
  PayableStatus.PENDING,
  PayableStatus.OPENED
];

/**
 * Build the tab's server-side `searchPayableRows` filter + sort. Ported from the
 * v1 `filter[status][in]` + `filter[payer.workflowStatus]` tab filters, mapped
 * onto the v3 `status` (raw lifecycle) + `payerApprovalStatus` fields so the
 * tabs still filter server-side and compose with pagination.
 *
 * Accepted approximations vs. v1 (recorded in V3_MIGRATION_TRACKER.md):
 * - The `PaymentInitiated`/`Funded` client-workflow states have no v3
 *   `payerApprovalStatus`, so the "scheduled" bucket is `Approved`-only.
 * - `internal.events.paymentDueAt` (payroll payment-due sort) has no v3 twin;
 *   the pre-payment tabs sort by `dueDate` instead.
 */
export function getTabFilter(
  tab: PayablesTabFilter,
  isDualStage = false
): PayableRowsSearchRequest {
  switch (tab) {
    case "draft":
      return {
        filter: { status: { anyOf: [PayableStatus.CREATED] } },
        sort: { updatedAt: ListFormsSortParameterValue.DESC }
      };
    case "to-approve":
      return {
        filter: {
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payerApprovalStatus: {
            anyOf: isDualStage
              ? [PayerApprovalStatus.PRE_APPROVED]
              : [PayerApprovalStatus.PENDING, PayerApprovalStatus.PRE_APPROVED]
          }
        },
        sort: { dueDate: ListFormsSortParameterValue.ASC }
      };
    case "to-preapprove":
      return {
        filter: {
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payerApprovalStatus: { anyOf: [PayerApprovalStatus.PENDING] }
        },
        sort: { dueDate: ListFormsSortParameterValue.ASC }
      };
    case "scheduled":
      return {
        filter: {
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payerApprovalStatus: { anyOf: [PayerApprovalStatus.APPROVED] }
        },
        sort: { dueDate: ListFormsSortParameterValue.ASC }
      };
    case "paid":
      return {
        filter: {
          status: {
            anyOf: [PayableStatus.PAID, PayableStatus.PAYMENT_IN_TRANSIT]
          }
        },
        sort: { paidAt: ListFormsSortParameterValue.DESC }
      };
    default:
      // "all" — everything except cancelled.
      return {
        filter: { status: { noneOf: [PayableStatus.CANCELLED] } },
        sort: { updatedAt: ListFormsSortParameterValue.DESC }
      };
  }
}

type RequestOptions = {
  isDualStage?: boolean;
  // Scopes the list to a single payroll run (the v1 `internal.payroll.payrollId`
  // filter). The payroll run is modelled as the payables' parent invoice.
  parentInvoiceId?: string;
};

const toSortValue = (
  direction: ReturnType<typeof getApiDirectionFromTable>
): ListFormsSortParameterValue | undefined => {
  if (direction === "asc") {
    return ListFormsSortParameterValue.ASC;
  }
  if (direction === "desc") {
    return ListFormsSortParameterValue.DESC;
  }
  return undefined;
};

export function createSearchRequest(
  filter: Filter | undefined,
  sort: UrlQuerySort<SortField> | undefined,
  tab: PayablesTabFilter,
  options?: RequestOptions
): PayableRowsSearchRequest {
  const tabRequest = getTabFilter(tab, options?.isDualStage);
  const request: PayableRowsSearchRequest = {
    filter: { ...tabRequest.filter },
    sort: tabRequest.sort
  };

  if (options?.parentInvoiceId) {
    request.filter.parentInvoiceId = { eq: options.parentInvoiceId };
  }

  if (filter?.payeeId) {
    request.filter.payeeId = { eq: filter.payeeId };
  }

  if (filter?.bulkBatchId) {
    request.filter.batchIds = { eq: filter.bulkBatchId };
  }

  if (filter?.dueDateRange) {
    const range = convertDateRangeToDates(filter.dueDateRange);
    if (range?.start || range?.end) {
      request.filter.dueDate = { gte: range?.start, lte: range?.end };
    }
  }

  if (filter?.paidDateRange) {
    const range = convertDateRangeToDates(filter.paidDateRange);
    if (range?.start || range?.end) {
      request.filter.events_paidAt = { gte: range?.start, lte: range?.end };
    }
  }

  if (filter?.openedDateRange) {
    const range = convertDateRangeToDates(filter.openedDateRange);
    if (range?.start || range?.end) {
      request.filter.events_openedAt = { gte: range?.start, lte: range?.end };
    }
  }

  // A user-selected column sort overrides the tab's default sort.
  const direction = toSortValue(getApiDirectionFromTable(sort?.direction));
  if (sort && direction) {
    if (sort.field === "createdAt") {
      request.sort = { createdAt: direction };
    } else if (sort.field === "updatedAt") {
      request.sort = { updatedAt: direction };
    } else if (sort.field === "events.paidAt") {
      request.sort = { paidAt: direction };
    }
  }

  return request;
}
